/* eslint-disable no-use-before-define */
import { useEffect } from 'react';
import { useApolloClient } from '@apollo/client';

import { formatIsoDate, subDays } from '..';
import FavMealsQuery from '../ApolloConnectors/FavMeals/Fetch/FavMealsQuery';
import {
  MEASUREMENT_TYPES,
  PERIOD_END,
  PERIOD_LENGTH,
} from '../ApolloConnectors/Measurements/consts';
import UserDataQuery from '../ApolloConnectors/UserData/UserDataQuery';
import CurrentMeasurementsQuery from '../ApolloConnectors/Measurements/CurrentMeasurements/CurrentMeasurementsQuery';
import MeasurementHistoryQuery from '../ApolloConnectors/Measurements/MeasurementHistory/MeasurementHistoryQuery';
import DietPlanQuery from '../ApolloConnectors/DailyDietPlan/Fetch/DietPlanQuery';
import DietSettingsQuery from '../ApolloConnectors/DietSettings/Fetch/DietSettingsQuery';

import clearDailyDietPlanCacheObjects from './clearDailyDietPlanCacheObjects';

const OnMessageCacheModifier = () => {
  const client = useApolloClient();

  useEffect(() => {
    window.addEventListener('message', (e) => {
      if (e.data.action === 'refresh_data') {
        switch (e.data.payload.module) {
          case 'user_settings':
            handleRefreshUserSettings();
            break;
          case 'hydration':
            handleRefreshHydration();
            break;
          case 'measurements':
            handleRefreshMeasurement();
            break;
          case 'tracker':
            handleRefreshTracker(e.data.payload?.date || null);
            break;
        }
      } else if (e.data.action === 'handle_favourite_meal') {
        const { key, mealId, isFavorite } = e.data.payload;
        handleFavouriteMeal(key, mealId, isFavorite);
      } else if (e.data.action === 'handle_eaten_meal') {
        const { key, isEaten } = e.data.payload;
        handleIsEatenMeal(key, isEaten);
      } else if (e.data.action === 'updated_intermittent_fasting') {
        handleIntermittentFasting();
      }
    });
  }, []);

  const handleRefreshTracker = (date) => {
    if (date) {
      client.cache.evict({
        id: `DailyTrackerData:{"date":"${date}"}`,
      });
    } else {
      const serializedState = client.cache.extract();
      const trackerListKeys = Object.keys(serializedState).filter((item) =>
        item.includes('DailyTrackerData')
      );

      trackerListKeys.forEach((key) => {
        client.cache.evict({
          id: key,
        });
      });
    }

    client.cache.gc();
  };

  const handleRefreshUserSettings = () => {
    // localStorage.removeItem('apollo-cache-persist');
    handleRefreshHydration();
    handleRefreshMeasurement(); // clear daily diet plan cache objects also
    handleRefreshDietSettings();

    // clearDailyDietPlanCacheObjects(client);

    // broadcast channel
    // if (post) {
    //   post('reload');
    // }
  };

  const handleRefreshHydration = () => {
    const userData = client.readQuery({
      query: UserDataQuery,
      variables: { date: formatIsoDate(new Date()) },
    });

    const userId = userData?.me?.id;

    if (userId) {
      client.cache.evict({
        id: `UserHydrationDay:{"key":"${userId}:${formatIsoDate(new Date())}"}`,
      });
      client.cache.gc();
    }
  };

  const handleRefreshMeasurement = () => {
    client.query({
      query: CurrentMeasurementsQuery,
      fetchPolicy: 'network-only',
    });
    client.query({
      query: MeasurementHistoryQuery,
      variables: {
        datePeriod: {
          start: formatIsoDate(subDays(PERIOD_END, PERIOD_LENGTH - 1)),
          end: formatIsoDate(PERIOD_END),
        },
        [MEASUREMENT_TYPES.WEIGHT]: true,
        [MEASUREMENT_TYPES.HIP]: true,
        [MEASUREMENT_TYPES.WAIST]: true,
        [MEASUREMENT_TYPES.THIGH]: true,
        [MEASUREMENT_TYPES.RIGHTTHIGH]: true,
        [MEASUREMENT_TYPES.CHEST]: true,
        [MEASUREMENT_TYPES.ARM]: true,
        [MEASUREMENT_TYPES.RIGHTARM]: true,
      },
      fetchPolicy: 'network-only',
    });
    clearDailyDietPlanCacheObjects(client);
    handleRefreshDietSettings();
  };

  const handleRefreshDietSettings = () => {
    client.query({
      query: DietSettingsQuery,
      variables: {
        date: formatIsoDate(new Date()),
      },
      fetchPolicy: 'network-only',
    });
  };

  const handleFavouriteMeal = (key, mealId, isFavorite) => {
    client.cache.modify({
      id: `UserDailyDietMeal:{"key":"${key}"}`,
      fields: {
        isFavorite() {
          return isFavorite;
        },
      },
    });

    client.cache.modify({
      id: `UserDailyDietPlanEventMeal:{"key":"${key}"}`,
      fields: {
        isFavorite() {
          return isFavorite;
        },
      },
    });

    if (!isFavorite) {
      client.cache.evict({ id: `DietMeal:${mealId}` });
      client.cache.gc();
    } else {
      client.query({
        query: FavMealsQuery,
        variables: { text: '', date: formatIsoDate(new Date()) },
        fetchPolicy: 'network-only',
      });
    }
  };

  const handleIsEatenMeal = (key, isEaten) => {
    client.cache.modify({
      id: `UserDailyDietMeal:{"key":"${key}"}`,
      fields: {
        isEaten() {
          return isEaten;
        },
      },
    });

    client.cache.modify({
      id: `UserDailyDietPlanEventMeal:{"key":"${key}"}`,
      fields: {
        isEaten() {
          return isEaten;
        },
      },
    });
  };

  const handleIntermittentFasting = () => {
    client.query({
      query: DietPlanQuery,
      variables: {
        today: formatIsoDate(new Date()),
      },
      fetchPolicy: 'network-only',
    });
  };

  return null;
};

export default OnMessageCacheModifier;
